import React, { useEffect, useState } from "react";
import { Button, Col, Form, Input, InputNumber, Modal, Radio, Row, message } from "antd";
import { EnvironmentOutlined } from "@ant-design/icons";
import type { MartFormValues } from "../types";
import type { Market } from "types/market";
import UploadLogoBox from "./UploadLogoBox";
import MarketLocationPicker, {
  MarketLocationValue,
  extractAddressDetails,
} from "./MarketLocationPicker";

const { TextArea } = Input;

export type MartFormModalProps = {
  open: boolean;
  mode: "add" | "edit";
  initialMart?: Market | null;
  loading?: boolean;
  onClose: () => void;
  onSubmit: (values: MartFormValues) => void;
};

const normFile = (e: any) => {
  if (Array.isArray(e)) return e;
  return e?.fileList ?? [];
};

const readCoordinate = (...values: Array<number | string | null | undefined>) => {
  const value = values.find(
    (candidate) => candidate !== null && candidate !== undefined && candidate !== ""
  );

  if (value === undefined || value === null || value === "") return null;

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const MartFormModal: React.FC<MartFormModalProps> = ({
  open,
  mode,
  initialMart,
  loading = false,
  onClose,
  onSubmit,
}) => {
  const [form] = Form.useForm<MartFormValues>();
  const [initialImageCleared, setInitialImageCleared] = useState(false);

  useEffect(() => {
    if (!open) return;
    setInitialImageCleared(false);

    if (mode === "edit" && initialMart) {
      const extracted = extractAddressDetails({ display_name: initialMart.location });
      form.setFieldsValue({
        name: initialMart.name,
        location: initialMart.location,
        city: initialMart.city || extracted.city || "",
        state: initialMart.state || extracted.state || "",
        lat: readCoordinate(initialMart.lat, initialMart.latitude),
        lng: readCoordinate(initialMart.lng, initialMart.longitude),
        description: initialMart.description,
        status: initialMart.status === "upcoming" ? "upcoming" : "active",
        image: [],
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        status: "active",
      });
    }
  }, [open, mode, initialMart, form]);

  const handleCancel = () => {
    onClose();
    form.resetFields();
  };

  return (
    <Modal
      title={mode === "edit" ? "Edit Market" : "Add Market"}
      open={open}
      onCancel={handleCancel}
      footer={null}
      centered
      width={780}
      destroyOnClose
      className="add-mart-modal modal-round"
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onSubmit}
        onFinishFailed={(info) => {
          const hasLocationError = info.errorFields.some((field) =>
            field.name.some(
              (part) =>
                part === "lat" ||
                part === "lng" ||
                part === "city" ||
                part === "state" ||
                part === "location"
            )
          );

          if (hasLocationError) {
            message.error("Please fill in all location details (address, city, state, lat/lng)");
          }
        }}
      >
        <Form.Item
          name="image"
          valuePropName="fileList"
          getValueFromEvent={normFile}
          rules={[
            {
              validator: (_, value) => {
                const hasNewFile = Array.isArray(value) && value.length > 0;
                const hasExistingLogo =
                  mode === "edit" && initialMart?.logo && !initialImageCleared;

                if (hasNewFile || hasExistingLogo) {
                  return Promise.resolve();
                }

                return Promise.reject(new Error("Please upload market logo"));
              },
            },
          ]}
        >
          <UploadLogoBox
            initialImageUrl={
              mode === "edit" && !initialImageCleared ? initialMart?.logo : undefined
            }
            onClearInitial={() => setInitialImageCleared(true)}
          />
        </Form.Item>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="name"
              label="Market Name"
              rules={[{ required: true, message: "Please enter market name" }]}
            >
              <Input
                placeholder="e.g. Sabon Gari Kano"
                size="large"
                className="input-round"
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name="location"
              label="Location / Address"
              rules={[{ required: true, message: "Please enter location" }]}
            >
              <Input
                placeholder="e.g. Ikeja City Mall Area, Alausa"
                prefix={<EnvironmentOutlined style={{ color: "#35b558" }} />}
                size="large"
                className="input-round"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="city"
              label="City"
              rules={[{ required: true, message: "Please enter city or select location on map" }]}
            >
              <Input
                placeholder="e.g. Ikeja"
                size="large"
                className="input-round"
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name="state"
              label="State"
              rules={[{ required: true, message: "Please enter state or select location on map" }]}
            >
              <Input
                placeholder="e.g. Lagos"
                size="large"
                className="input-round"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="lat"
              label="Latitude"
              rules={[
                {
                  required: true,
                  message: "Please select market location from map",
                },
              ]}
            >
              <InputNumber
                style={{ width: "100%" }}
                placeholder="e.g. 6.601838"
                size="large"
                className="input-round"
                step="any"
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name="lng"
              label="Longitude"
              rules={[
                {
                  required: true,
                  message: "Please select market location from map",
                },
              ]}
            >
              <InputNumber
                style={{ width: "100%" }}
                placeholder="e.g. 3.351486"
                size="large"
                className="input-round"
                step="any"
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          shouldUpdate={(prev, next) =>
            prev.location !== next.location ||
            prev.city !== next.city ||
            prev.state !== next.state ||
            prev.lat !== next.lat ||
            prev.lng !== next.lng
          }
          className="market-location-form-item"
        >
          {({ getFieldValue, setFieldsValue }) => {
            const pickerValue: MarketLocationValue = {
              address: getFieldValue("location"),
              city: getFieldValue("city"),
              state: getFieldValue("state"),
              lat: getFieldValue("lat"),
              lng: getFieldValue("lng"),
            };

            return (
              <MarketLocationPicker
                mode={mode}
                value={pickerValue}
                onChange={(value) => {
                  setFieldsValue({
                    location: value.address || getFieldValue("location") || "",
                    city: value.city || getFieldValue("city") || "",
                    state: value.state || getFieldValue("state") || "",
                    lat: value.lat,
                    lng: value.lng,
                  });
                }}
              />
            );
          }}
        </Form.Item>

        <Row gutter={16}>
          <Col xs={24}>
            <Form.Item
              name="status"
              label="Status"
              rules={[{ required: true, message: "Please select market status" }]}
              initialValue="active"
            >
              <Radio.Group buttonStyle="solid" className="market-status-radio-group">
                <Radio.Button value="active">Active</Radio.Button>
                <Radio.Button value="upcoming">Upcoming</Radio.Button>
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="description"
          label="Description"
          rules={[{ required: true, message: "Please enter description" }]}
        >
          <TextArea
            placeholder="Enter market description"
            rows={4}
            size="large"
            className="input-round textarea-round"
          />
        </Form.Item>

        <Form.Item className="mb-0 add-mart-modal-footer">
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            block
            className="markets-add-btn btn-round"
            loading={loading}
            disabled={loading}
          >
            {mode === "edit" ? "Update Market" : "Add Market"}
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default MartFormModal;
